import {
  adminControlPlaneUrl,
  adminControlPlaneWebSocketUrl,
  buildControlPlaneHeaders,
  managedAppUrl,
} from "./runtime";
import type {
  StatusResponse,
  ServerInfo,
  User,
  Project,
  ProjectBlockCacheDetail,
  CacheEntrySummary,
  CacheEntryDetail,
  CacheEntryStatus,
  MaintenanceTaskDescriptor,
  MaintenanceTaskLatestSummary,
  MaintenanceTaskResult,
  MaintenanceTaskScope,
  MaintenanceTaskRun,
  MaintenanceTaskRunListResponse,
  TableInfo,
  ColumnInfo,
  IndexInfo,
  FKInfo,
  QueryResult,
  MigrationDetail,
  LogEntry,
  SessionInfo,
  AuthSessionInfo,
  MigrationStatus,
  ProjectMember,
  Organization,
  OrganizationMember,
  OrganizationAuthProvider,
  PlatformAuthProvider,
  TrackingSettings,
  OrganizationInvite,
  RealtimeStats,
  DebugSessionInfo,
  DebugMessagesResponse,
  AdminControlPlaneSnapshot,
  AdminAuditEntry,
  SystemOperatorSession,
  RealtimeDebugResponse,
  OrganizationRegistrationRequest,
  ErrorLogEntry,
  ErrorLogListResponse,
  ErrorLogSource,
  RequestTraceDetail,
  RequestTraceListResponse,
  RequestTraceSource,
  RequestTraceTriggerSource,
  TaskQueueJobDetail,
  TaskQueueJobListResponse,
  TaskQueueJobStatus,
  TaskQueueProcessResult,
  TaskQueueBulkCancelResult,
  TaskQueueDeleteResult,
  CoverageProcessingSettings,
  TaskQueueDefinitionSettings,
  TaskQueueExecutionSettings,
  TaskQueueStats,
  TaskQueueEvent,
  TaskQueueExecutionRun,
  TaskQueueExecutionRunDetail,
  TaskQueuePage,
  TaskQueueCursorPage,
  TaskQueueScheduler,
  TaskQueueSchedulerHealth,
  TaskQueueSchedulerHourlyStat,
  TaskQueueSchedulerInput,
  TaskQueueSchedulerTarget,
  TaskQueueDriverState,
  TaskQueueDriverMode,
  TaskQueueSchedulerTick,
  TaskQueueTestTaskInput,
  TaskQueueTestTaskResponse,
  MaintenanceQueueHistoryCleanupResult,
  MaintenanceQueueHistoryRecordType,
  MaintenanceQueueHistoryStatus,
  TaskQueueTaskDelta,
  TaskQueueSchedulerDelta,
} from "./types";

export class ControlPlaneRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number | null,
    public readonly retryable: boolean,
  ) {
    super(message);
    this.name = "ControlPlaneRequestError";
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { signal?: AbortSignal },
): Promise<T> {
  const timeout = new AbortController();
  const timeoutId = window.setTimeout(
    () => timeout.abort("Request timed out"),
    10_000,
  );
  const signal = options?.signal
    ? AbortSignal.any([options.signal, timeout.signal])
    : timeout.signal;
  const opts: RequestInit = {
    method,
    headers: buildControlPlaneHeaders(),
    signal,
  };
  if (body !== undefined) opts.body = JSON.stringify(body);
  else if (method !== "GET") opts.body = "{}";
  try {
    const res = await fetch(adminControlPlaneUrl(path), opts);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new ControlPlaneRequestError(
        err.error ?? `HTTP ${res.status}`,
        res.status,
        res.status === 408 || res.status === 429 || res.status >= 500,
      );
    }
    return res.json() as Promise<T>;
  } catch (error) {
    if (error instanceof ControlPlaneRequestError) throw error;
    if (options?.signal?.aborted) throw error;
    const message = timeout.signal.aborted
      ? "Request timed out after 10 seconds"
      : error instanceof Error
        ? error.message
        : "Network request failed";
    throw new ControlPlaneRequestError(message, null, true);
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function apiFetch<T>(method: string, path: string): Promise<T> {
  const res = await fetch(managedAppUrl(path), {
    method,
    headers: buildControlPlaneHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

async function managedAppFetch<T = any>(
  path: string,
  headers: Record<string, string>,
): Promise<T> {
  const res = await fetch(managedAppUrl(path), { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  status: () => request<StatusResponse>("GET", "/status"),
  controlPlane: {
    info: () =>
      request<AdminControlPlaneSnapshot>("GET", "/control-plane/info"),
    login: (email: string, password: string) =>
      request<SystemOperatorSession>("POST", "/control-plane/login", {
        email,
        password,
      }),
    session: () =>
      request<SystemOperatorSession>("GET", "/control-plane/session"),
    logout: () =>
      request<{ success: boolean }>("POST", "/control-plane/logout"),
    audit: (limit: number = 50) =>
      request<AdminAuditEntry[]>("GET", `/control-plane/audit?limit=${limit}`),
  },
  servers: {
    list: () => request<ServerInfo[]>("GET", "/servers"),
    start: (name: string, port?: number) =>
      request<{ success: boolean; pid: number; port: number }>(
        "POST",
        `/servers/${name}/start`,
        port ? { port } : undefined,
      ),
    stop: (name: string) =>
      request<{ success: boolean }>("POST", `/servers/${name}/stop`),
    logs: (name: string) => request<LogEntry[]>("GET", `/servers/${name}/logs`),
  },
  users: {
    list: (filters?: {
      role?: string;
      status?: string;
      organizationId?: string;
    }) => {
      const params = new URLSearchParams();
      if (filters?.role) params.set("role", filters.role);
      if (filters?.status) params.set("status", filters.status);
      if (filters?.organizationId)
        params.set("organizationId", filters.organizationId);
      const qs = params.toString();
      return request<User[]>("GET", `/users${qs ? `?${qs}` : ""}`);
    },
    create: (data: {
      email: string;
      displayName: string;
      password?: string;
      organizationName?: string;
    }) =>
      request<{ id: string; email: string; organizationId?: string }>(
        "POST",
        "/users",
        data,
      ),
    update: (
      id: string,
      data: {
        role?: string;
        status?: string;
        displayName?: string;
        profileLocked?: boolean;
      },
    ) => request<{ success: boolean }>("PUT", `/users/${id}`, data),
    resetPassword: (id: string, password: string) =>
      request<{ success: boolean }>("PUT", `/users/${id}/password`, {
        password,
      }),
    loginLink: (id: string, organizationId?: string, expirySeconds?: number) =>
      request<{ link: string; token: string; expiresAt: string }>(
        "POST",
        `/users/${id}/login-link`,
        organizationId || expirySeconds
          ? { organizationId, expirySeconds }
          : undefined,
      ),
    emailVerificationLink: (id: string, expiresInHours?: number) =>
      request<{ link: string; token: string; expiresAt: string }>(
        "POST",
        `/users/${id}/email-verification-link`,
        expiresInHours ? { expiresInHours } : undefined,
      ),
    verifyEmail: (id: string) =>
      request<{ success: boolean }>("PUT", `/users/${id}/verify-email`),
    unverifyEmail: (id: string) =>
      request<{ success: boolean }>("PUT", `/users/${id}/unverify-email`),
  },
  sessions: {
    list: (opts?: {
      organizationId?: string;
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (opts?.organizationId)
        params.set("organizationId", opts.organizationId);
      if (opts?.limit) params.set("limit", String(opts.limit));
      if (opts?.offset) params.set("offset", String(opts.offset));
      const qs = params.toString();
      return request<{ items: SessionInfo[]; total: number }>(
        "GET",
        `/sessions${qs ? `?${qs}` : ""}`,
      );
    },
    revoke: (id: string) =>
      request<{ success: boolean }>("DELETE", `/sessions/${id}`),
    authList: (opts?: {
      status?: string;
      limit?: number;
      offset?: number;
      organizationId?: string;
    }) => {
      const params = new URLSearchParams();
      if (opts?.status) params.set("status", opts.status);
      if (opts?.limit) params.set("limit", String(opts.limit));
      if (opts?.offset) params.set("offset", String(opts.offset));
      if (opts?.organizationId)
        params.set("organizationId", opts.organizationId);
      const qs = params.toString();
      return request<{ items: AuthSessionInfo[]; total: number }>(
        "GET",
        `/auth-sessions${qs ? `?${qs}` : ""}`,
      );
    },
    authRevoke: (id: string, alsoRevokeRefreshToken?: boolean) =>
      request<{ success: boolean }>(
        "DELETE",
        `/auth-sessions/${id}${alsoRevokeRefreshToken ? "?alsoRevokeRefreshToken=1" : ""}`,
      ),
  },
  emailVerificationLinks: {
    list: (opts?: {
      organizationId?: string;
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (opts?.organizationId)
        params.set("organizationId", opts.organizationId);
      if (opts?.limit) params.set("limit", String(opts.limit));
      if (opts?.offset) params.set("offset", String(opts.offset));
      const qs = params.toString();
      return request<{
        items: import("./types").EmailVerificationLinkInfo[];
        total: number;
      }>("GET", `/email-verification-links${qs ? `?${qs}` : ""}`);
    },
    revoke: (id: string) =>
      request<{ success: boolean }>(
        "DELETE",
        `/email-verification-links/${id}`,
      ),
  },
  refreshTokens: {
    list: (opts?: {
      organizationId?: string;
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (opts?.organizationId)
        params.set("organizationId", opts.organizationId);
      if (opts?.limit) params.set("limit", String(opts.limit));
      if (opts?.offset) params.set("offset", String(opts.offset));
      const qs = params.toString();
      return request<{
        items: import("./types").RefreshTokenInfo[];
        total: number;
      }>("GET", `/refresh-tokens${qs ? `?${qs}` : ""}`);
    },
    revoke: (id: string) =>
      request<{ success: boolean }>("DELETE", `/refresh-tokens/${id}`),
    extend: (id: string, additionalMinutes: number) =>
      request<{ newExpiresAt: string }>("PUT", `/refresh-tokens/${id}/extend`, {
        additionalMinutes,
      }),
  },
  platformAuth: {
    list: () => request<PlatformAuthProvider[]>("GET", "/auth-providers"),
    update: (
      providerId: string,
      data: { enabled?: number; config?: Record<string, string> },
    ) =>
      request<{ success: boolean }>(
        "PUT",
        `/auth-providers/${providerId}`,
        data,
      ),
  },
  settings: {
    tracking: () => request<TrackingSettings>("GET", "/settings/tracking"),
    updateTracking: (data: TrackingSettings) =>
      request<{ success: boolean; settings: TrackingSettings }>(
        "PUT",
        "/settings/tracking",
        data,
      ),
  },
  projects: {
    list: (filters?: { userId?: string }) => {
      const params = new URLSearchParams();
      if (filters?.userId) params.set("userId", filters.userId);
      const qs = params.toString();
      return request<Project[]>("GET", `/projects${qs ? `?${qs}` : ""}`);
    },
    members: (id: string) =>
      request<ProjectMember[]>("GET", `/projects/${id}/members`),
    updateMember: (
      projectId: string,
      userId: string,
      data: { role?: string; permissions?: Record<string, boolean> },
    ) =>
      request<{ success: boolean }>(
        "PUT",
        `/projects/${projectId}/members/${userId}`,
        data,
      ),
    create: (data: {
      name: string;
      description?: string;
      visibility: string;
      blockSize: number;
      accessMode?: string;
      ownerId?: string;
      organizationId?: string;
      slug?: string;
      trackingThresholdOverrideEnabled?: boolean;
      trackingDistanceThresholdMeters?: number | null;
      trackingTimeThresholdSeconds?: number | null;
      trackingTimeThresholdBehavior?:
        "live_only" | "persist_and_live" | "no_update" | null;
    }) =>
      request<{ id: string; slug: string; name: string }>(
        "POST",
        "/projects",
        data,
      ),
    update: (
      id: string,
      data: {
        name?: string;
        description?: string;
        lifecycle?: string;
        visibility?: string;
        blockSize?: number;
        ownerId?: string;
        organizationId?: string;
        accessMode?: string;
        slug?: string;
        trackingThresholdOverrideEnabled?: boolean;
        trackingDistanceThresholdMeters?: number | null;
        trackingTimeThresholdSeconds?: number | null;
        trackingTimeThresholdBehavior?:
          "live_only" | "persist_and_live" | "no_update" | null;
      },
    ) => request<{ success: boolean }>("PUT", `/projects/${id}`, data),
    delete: (id: string) =>
      request<{ success: boolean }>("DELETE", `/projects/${id}`),
    invalidateBlockCache: (id: string) =>
      request<{ success: boolean }>(
        "POST",
        `/projects/${id}/block-cache/invalidate`,
      ),
    blockCache: (id: string) =>
      request<ProjectBlockCacheDetail>("GET", `/projects/${id}/block-cache`),
    addMember: (projectId: string, userId: string, role?: string) =>
      request<{ success: boolean }>("POST", `/projects/${projectId}/members`, {
        userId,
        role,
      }),
    removeMember: (projectId: string, userId: string) =>
      request<{ success: boolean }>(
        "DELETE",
        `/projects/${projectId}/members/${userId}`,
      ),
    createInvite: (projectId: string, data: { email: string; role?: string }) =>
      request<{ id: string; token: string }>(
        "POST",
        `/projects/${projectId}/invites`,
        data,
      ),
    listInvites: (projectId: string) =>
      request<
        {
          id: string;
          email: string;
          role: string;
          token: string;
          createdBy: string;
          consumedAt: string | null;
          createdAt: string;
        }[]
      >("GET", `/projects/${projectId}/invites`),
    removeInvite: (projectId: string, inviteId: string) =>
      request<{ success: boolean }>(
        "DELETE",
        `/projects/${projectId}/invites/${inviteId}`,
      ),
    realtimeDebug: (projectId: string, tokenOverride?: string) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (tokenOverride) headers.Authorization = `Bearer ${tokenOverride}`;
      return managedAppFetch(`/projects/${projectId}/realtime/debug`, headers);
    },
    generateToken: (projectId: string, userId: string) =>
      request<{
        token?: string | null;
        userId: string | null;
        userName: string | null;
        organizationId: string | null;
        projectId: string;
      }>("POST", `/projects/${projectId}/token`, { userId }),
  },
  organizations: {
    list: () => request<Organization[]>("GET", "/organizations"),
    registrationRequests: () =>
      request<OrganizationRegistrationRequest[]>(
        "GET",
        "/organization-registration-requests",
      ),
    approveRegistrationRequest: (id: string) =>
      request<{ success: boolean; request: OrganizationRegistrationRequest }>(
        "POST",
        `/organization-registration-requests/${id}/approve`,
      ),
    rejectRegistrationRequest: (id: string, reviewNote?: string) =>
      request<{ success: boolean; request: OrganizationRegistrationRequest }>(
        "POST",
        `/organization-registration-requests/${id}/reject`,
        reviewNote ? { reviewNote } : {},
      ),
    create: (data: { name: string; ownerUserId: string; slug?: string }) =>
      request<{ id: string; slug: string }>("POST", "/organizations", data),
    update: (id: string, data: { name?: string; slug?: string }) =>
      request<{ success: boolean }>("PUT", `/organizations/${id}`, data),
    members: (id: string, opts?: { limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (opts?.limit) params.set("limit", String(opts.limit));
      if (opts?.offset) params.set("offset", String(opts.offset));
      const qs = params.toString();
      return request<{ items: OrganizationMember[]; total: number }>(
        "GET",
        `/organizations/${id}/members${qs ? `?${qs}` : ""}`,
      );
    },
    addMember: (orgId: string, userId: string, role?: string) =>
      request<{ success: boolean }>("POST", `/organizations/${orgId}/members`, {
        userId,
        role,
      }),
    updateMember: (
      orgId: string,
      userId: string,
      data: {
        role?: string;
        permissions?: Record<string, boolean>;
        status?: string;
      },
    ) =>
      request<{ success: boolean }>(
        "PUT",
        `/organizations/${orgId}/members/${userId}`,
        data,
      ),
    removeMember: (orgId: string, userId: string) =>
      request<{ success: boolean }>(
        "DELETE",
        `/organizations/${orgId}/members/${userId}`,
      ),
    authProviders: (id: string) =>
      request<OrganizationAuthProvider[]>(
        "GET",
        `/organizations/${id}/auth-providers`,
      ),
    updateAuthProvider: (
      orgId: string,
      providerId: string,
      data: { enabled?: number; config?: string },
    ) =>
      request<{ success: boolean }>(
        "PUT",
        `/organizations/${orgId}/auth-providers/${providerId}`,
        data,
      ),
    orgUsers: (
      id: string,
      role?: string,
      opts?: { limit?: number; offset?: number },
    ) => {
      const params = new URLSearchParams();
      if (role) params.set("role", role);
      if (opts?.limit) params.set("limit", String(opts.limit));
      if (opts?.offset) params.set("offset", String(opts.offset));
      const qs = params.toString();
      return request<{ items: User[]; total: number }>(
        "GET",
        `/organizations/${id}/users${qs ? `?${qs}` : ""}`,
      );
    },
    listManagers: (orgId: string) =>
      request<Array<{ userId: string; name: string; role: string }>>(
        "GET",
        `/organizations/${orgId}/managers`,
      ),
    invites: (id: string, opts?: { limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (opts?.limit) params.set("limit", String(opts.limit));
      if (opts?.offset) params.set("offset", String(opts.offset));
      const qs = params.toString();
      return request<{ items: OrganizationInvite[]; total: number }>(
        "GET",
        `/organizations/${id}/invites${qs ? `?${qs}` : ""}`,
      );
    },
    createInvite: (
      orgId: string,
      data: { email: string; role?: string; createdBy: string },
    ) =>
      request<{ id: string; token: string; link: string }>(
        "POST",
        `/organizations/${orgId}/invites`,
        data,
      ),
    removeInvite: (orgId: string, inviteId: string) =>
      request<{ success: boolean }>(
        "DELETE",
        `/organizations/${orgId}/invites/${inviteId}`,
      ),
    projects: (id: string) =>
      request<Project[]>("GET", `/organizations/${id}/projects`),
    invalidateProjectBlockCaches: (id: string) =>
      request<{ success: boolean; invalidatedProjects: number }>(
        "POST",
        `/organizations/${id}/block-caches/invalidate`,
      ),
    invalidateCaches: (id: string, cacheType?: string) =>
      request<{ success: boolean; invalidated: number }>(
        "POST",
        `/organizations/${id}/caches/invalidate`,
        cacheType ? { cacheType } : {},
      ),
  },
  caches: {
    list: (filters?: {
      organizationId?: string;
      projectId?: string;
      cacheType?: string;
      endpoint?: string;
      status?: CacheEntryStatus;
    }) => {
      const params = new URLSearchParams();
      if (filters?.organizationId)
        params.set("organizationId", filters.organizationId);
      if (filters?.projectId) params.set("projectId", filters.projectId);
      if (filters?.cacheType) params.set("cacheType", filters.cacheType);
      if (filters?.endpoint) params.set("endpoint", filters.endpoint);
      if (filters?.status) params.set("status", filters.status);
      const qs = params.toString();
      return request<CacheEntrySummary[]>(
        "GET",
        `/caches${qs ? `?${qs}` : ""}`,
      );
    },
    get: (id: string) => request<CacheEntryDetail>("GET", `/caches/${id}`),
    invalidate: (id: string) =>
      request<{ success: boolean }>("POST", `/caches/${id}/invalidate`),
    invalidateProject: (projectId: string, cacheType?: string) =>
      request<{ success: boolean; invalidated: number }>(
        "POST",
        `/projects/${projectId}/caches/invalidate`,
        cacheType ? { cacheType } : {},
      ),
  },
  maintenance: {
    tasks: () =>
      request<MaintenanceTaskDescriptor[]>("GET", "/maintenance/tasks"),
    latestRuns: () =>
      request<MaintenanceTaskLatestSummary[]>(
        "GET",
        "/maintenance/latest-runs",
      ),
    runs: (filters?: {
      taskId?: string;
      status?: "completed" | "failed";
      page?: number;
      perPage?: number;
    }) => {
      const params = new URLSearchParams();
      if (filters?.taskId) params.set("taskId", filters.taskId);
      if (filters?.status) params.set("status", filters.status);
      if (typeof filters?.page === "number")
        params.set("page", String(filters.page));
      if (typeof filters?.perPage === "number")
        params.set("perPage", String(filters.perPage));
      const qs = params.toString();
      return request<MaintenanceTaskRunListResponse>(
        "GET",
        `/maintenance/runs${qs ? `?${qs}` : ""}`,
      );
    },
    preview: (taskId: string, scope?: MaintenanceTaskScope) =>
      request<MaintenanceTaskResult>(
        "POST",
        `/maintenance/tasks/${taskId}/preview`,
        scope ?? {},
      ),
    run: (taskId: string, scope?: MaintenanceTaskScope) =>
      request<MaintenanceTaskResult>(
        "POST",
        `/maintenance/tasks/${taskId}/run`,
        scope ?? {},
      ),
  },
  taskQueue: {
    stats: () => request<TaskQueueStats>("GET", "/task-queues/stats"),
    settings: () =>
      request<TaskQueueExecutionSettings>("GET", "/task-queues/settings"),
    updateSettings: (data: Partial<TaskQueueExecutionSettings>) =>
      request<TaskQueueExecutionSettings>("PUT", "/task-queues/settings", data),
    queueSettings: (queueName: string) =>
      request<TaskQueueDefinitionSettings>(
        "GET",
        `/task-queues/queues/${encodeURIComponent(queueName)}/settings`,
      ),
    updateQueueSettings: (
      queueName: string,
      data: Partial<TaskQueueDefinitionSettings>,
    ) =>
      request<TaskQueueDefinitionSettings>(
        "PUT",
        `/task-queues/queues/${encodeURIComponent(queueName)}/settings`,
        data,
      ),
    coverageSettings: () =>
      request<CoverageProcessingSettings>("GET", "/coverage/settings"),
    updateCoverageSettings: (data: Partial<CoverageProcessingSettings>) =>
      request<CoverageProcessingSettings>("PUT", "/coverage/settings", data),
    tasks: (filters?: {
      queueName?: string;
      taskType?: string;
      projectId?: string;
      generationId?: string;
      status?: TaskQueueJobStatus;
      from?: string;
      to?: string;
      page?: number;
      perPage?: number;
      cursor?: string;
      signal?: AbortSignal;
    }) => {
      const params = new URLSearchParams();
      if (filters?.queueName) params.set("queueName", filters.queueName);
      if (filters?.taskType) params.set("taskType", filters.taskType);
      if (filters?.projectId) params.set("projectId", filters.projectId);
      if (filters?.generationId)
        params.set("generationId", filters.generationId);
      if (filters?.status) params.set("status", filters.status);
      if (filters?.from) params.set("from", filters.from);
      if (filters?.to) params.set("to", filters.to);
      if (filters?.page) params.set("page", String(filters.page));
      if (filters?.perPage) params.set("perPage", String(filters.perPage));
      if (filters?.cursor) params.set("cursor", filters.cursor);
      const qs = params.toString();
      return request<TaskQueueJobListResponse>(
        "GET",
        `/task-queues/tasks${qs ? `?${qs}` : ""}`,
        undefined,
        { signal: filters?.signal },
      );
    },
    taskChanges: (filters: {
      after: number;
      queueName?: string;
      taskType?: string;
      projectId?: string;
      generationId?: string;
      status?: TaskQueueJobStatus;
      from?: string;
      to?: string;
      signal?: AbortSignal;
    }) => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(
        ([key, value]) =>
          key !== "signal" &&
          value != null &&
          value !== "" &&
          params.set(key, String(value)),
      );
      return request<TaskQueueTaskDelta>(
        "GET",
        `/task-queues/task-changes?${params}`,
        undefined,
        { signal: filters.signal },
      );
    },
    task: (id: string) =>
      request<TaskQueueJobDetail>("GET", `/task-queues/tasks/${id}`),
    pause: (name: string) =>
      request<{ success: boolean }>(
        "POST",
        `/task-queues/queues/${encodeURIComponent(name)}/pause`,
      ),
    resume: (name: string) =>
      request<{ success: boolean }>(
        "POST",
        `/task-queues/queues/${encodeURIComponent(name)}/resume`,
      ),
    process: (queueName = "coverage", limit = 1) =>
      request<TaskQueueProcessResult>("POST", "/task-queues/process", {
        queueName,
        limit,
      }),
    processTask: (id: string) =>
      request<TaskQueueProcessResult>(
        "POST",
        `/task-queues/tasks/${encodeURIComponent(id)}/process`,
      ),
    retry: (id: string) =>
      request<{ success: boolean }>("POST", `/task-queues/tasks/${id}/retry`),
    cancel: (id: string) =>
      request<{ success: boolean }>("POST", `/task-queues/tasks/${id}/cancel`),
    delete: (id: string) =>
      request<{ success: boolean }>("DELETE", `/task-queues/tasks/${id}`),
    cancelCoverage: (filters: {
      projectId?: string;
      generationId?: string;
      includeRunning?: boolean;
    }) =>
      request<TaskQueueBulkCancelResult>(
        "POST",
        "/task-queues/coverage/cancel",
        filters,
      ),
    deleteTerminal: (filters: {
      olderThan: string;
      statuses?: TaskQueueJobStatus[];
      limit?: number;
      queueName?: string;
      taskType?: string;
      projectId?: string;
      generationId?: string;
    }) =>
      request<TaskQueueDeleteResult>(
        "POST",
        "/task-queues/tasks/purge-terminal",
        filters,
      ),
    coverageGenerations: (projectId?: string) =>
      request<Array<Record<string, unknown>>>(
        "GET",
        `/task-queues/coverage-generations${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`,
      ),
    events: (after = 0, limit = 500) =>
      request<{ items: TaskQueueEvent[]; cursor: number }>(
        "GET",
        `/task-queues/events?after=${after}&limit=${limit}`,
      ),
    streamTicket: () =>
      request<{ ticket: string; expiresAt: string }>(
        "POST",
        "/task-queues/stream-ticket",
      ),
    streamUrl: (ticket: string, after = 0) => {
      const url = new URL(adminControlPlaneWebSocketUrl("/task-queues/stream"));
      url.searchParams.set("ticket", ticket);
      url.searchParams.set("after", String(after));
      return url.toString();
    },
    schedulers: (target?: TaskQueueSchedulerTarget, signal?: AbortSignal) =>
      request<TaskQueueScheduler[]>(
        "GET",
        `/task-queues/schedulers${target ? `?target=${target}` : ""}`,
        undefined,
        { signal },
      ),
    drivers: (signal?: AbortSignal) =>
      request<TaskQueueDriverState[]>(
        "GET",
        "/task-queues/drivers",
        undefined,
        { signal },
      ),
    updateDriver: (
      target: TaskQueueSchedulerTarget,
      input: { mode: TaskQueueDriverMode; fixedIntervalSeconds?: number },
    ) =>
      request<TaskQueueDriverState>(
        "PUT",
        `/task-queues/drivers/${target}`,
        input,
      ),
    wakeDriver: (target: TaskQueueSchedulerTarget) =>
      request<TaskQueueDriverState>(
        "POST",
        `/task-queues/drivers/${target}/wake`,
      ),
    enqueueTestTask: (input: TaskQueueTestTaskInput) =>
      request<TaskQueueTestTaskResponse>(
        "POST",
        "/task-queues/test-task",
        input,
      ),
    createScheduler: (input: TaskQueueSchedulerInput) =>
      request<TaskQueueScheduler>("POST", "/task-queues/schedulers", input),
    updateScheduler: (id: string, input: Partial<TaskQueueSchedulerInput>) =>
      request<TaskQueueScheduler>(
        "PUT",
        `/task-queues/schedulers/${encodeURIComponent(id)}`,
        input,
      ),
    deleteScheduler: (id: string) =>
      request<{ success: boolean }>(
        "DELETE",
        `/task-queues/schedulers/${encodeURIComponent(id)}`,
      ),
    runScheduler: (id: string) =>
      request<TaskQueueProcessResult>(
        "POST",
        `/task-queues/schedulers/${encodeURIComponent(id)}/run`,
      ),
    previewScheduler: (input: TaskQueueSchedulerInput) =>
      request<{ nextRuns: string[] }>(
        "POST",
        "/task-queues/schedulers/preview",
        input,
      ),
    schedulerHealth: (signal?: AbortSignal) =>
      request<TaskQueueSchedulerHealth[]>(
        "GET",
        "/task-queues/scheduler-health",
        undefined,
        { signal },
      ),
    schedulerChanges: (after: number, signal?: AbortSignal) =>
      request<TaskQueueSchedulerDelta>(
        "GET",
        `/task-queues/scheduler-changes?after=${Math.max(0, Math.floor(after))}`,
        undefined,
        { signal },
      ),
    schedulerHourly: (signal?: AbortSignal) =>
      request<TaskQueueSchedulerHourlyStat[]>(
        "GET",
        "/task-queues/scheduler-hourly",
        undefined,
        { signal },
      ),
    schedulerTicks: (
      filters?: {
        target?: TaskQueueSchedulerTarget;
        status?: string;
        workerId?: string;
        from?: string;
        to?: string;
        work?: "yes" | "no";
        page?: number;
        perPage?: number;
        cursor?: string;
      },
      signal?: AbortSignal,
    ) => {
      const params = new URLSearchParams();
      Object.entries(filters ?? {}).forEach(
        ([key, value]) => value != null && params.set(key, String(value)),
      );
      return request<TaskQueueCursorPage<TaskQueueSchedulerTick>>(
        "GET",
        `/task-queues/scheduler-ticks${params.size ? `?${params}` : ""}`,
        undefined,
        { signal },
      );
    },
    executionRuns: (
      filters?: {
        target?: TaskQueueSchedulerTarget;
        status?: string;
        schedulerId?: string;
        queueName?: string;
        triggerKind?: string;
        workerId?: string;
        from?: string;
        to?: string;
        work?: "yes" | "no";
        page?: number;
        perPage?: number;
        cursor?: string;
      },
      signal?: AbortSignal,
    ) => {
      const params = new URLSearchParams();
      Object.entries(filters ?? {}).forEach(
        ([key, value]) => value != null && params.set(key, String(value)),
      );
      return request<TaskQueueCursorPage<TaskQueueExecutionRun>>(
        "GET",
        `/task-queues/execution-runs${params.size ? `?${params}` : ""}`,
        undefined,
        { signal },
      );
    },
    executionRun: (id: string) =>
      request<TaskQueueExecutionRunDetail>(
        "GET",
        `/task-queues/execution-runs/${encodeURIComponent(id)}`,
      ),
    cleanupExecutionHistory: (input: {
      recordTypes: MaintenanceQueueHistoryRecordType[];
      statuses?: MaintenanceQueueHistoryStatus[];
      emptyOnly?: boolean;
      workOnly?: boolean;
      allMatches?: boolean;
      scheduledOnly?: boolean;
      olderThan: string;
      limit?: number;
      previewLimit?: number;
      dryRun?: boolean;
    }) =>
      request<MaintenanceQueueHistoryCleanupResult>(
        "POST",
        "/task-queues/execution-history/cleanup",
        input,
      ),
  },
  errors: {
    list: (filters?: {
      source?: ErrorLogSource;
      statusCode?: number;
      path?: string;
      organizationId?: string;
      projectId?: string;
      from?: string;
      to?: string;
      page?: number;
      perPage?: number;
    }) => {
      const params = new URLSearchParams();
      if (filters?.source) params.set("source", filters.source);
      if (typeof filters?.statusCode === "number")
        params.set("statusCode", String(filters.statusCode));
      if (filters?.path) params.set("path", filters.path);
      if (filters?.organizationId)
        params.set("organizationId", filters.organizationId);
      if (filters?.projectId) params.set("projectId", filters.projectId);
      if (filters?.from) params.set("from", filters.from);
      if (filters?.to) params.set("to", filters.to);
      if (typeof filters?.page === "number")
        params.set("page", String(filters.page));
      if (typeof filters?.perPage === "number")
        params.set("perPage", String(filters.perPage));
      const qs = params.toString();
      return request<ErrorLogListResponse>(
        "GET",
        `/errors${qs ? `?${qs}` : ""}`,
      );
    },
    get: (id: string) => request<ErrorLogEntry>("GET", `/errors/${id}`),
  },
  performance: {
    traces: (filters?: {
      requestIds?: string[];
      source?: RequestTraceSource;
      method?: string;
      path?: string;
      statusCode?: number;
      organizationId?: string;
      projectId?: string;
      triggerSource?: RequestTraceTriggerSource;
      minDurationMs?: number;
      page?: number;
      perPage?: number;
    }) => {
      const params = new URLSearchParams();
      for (const requestId of filters?.requestIds ?? []) {
        const normalized = requestId.trim();
        if (normalized) params.append("requestId", normalized);
      }
      if (filters?.source) params.set("source", filters.source);
      if (filters?.method) params.set("method", filters.method);
      if (filters?.path) params.set("path", filters.path);
      if (typeof filters?.statusCode === "number")
        params.set("statusCode", String(filters.statusCode));
      if (filters?.organizationId)
        params.set("organizationId", filters.organizationId);
      if (filters?.projectId) params.set("projectId", filters.projectId);
      if (filters?.triggerSource)
        params.set("triggerSource", filters.triggerSource);
      if (typeof filters?.minDurationMs === "number")
        params.set("minDurationMs", String(filters.minDurationMs));
      if (typeof filters?.page === "number")
        params.set("page", String(filters.page));
      if (typeof filters?.perPage === "number")
        params.set("perPage", String(filters.perPage));
      const qs = params.toString();
      return request<RequestTraceListResponse>(
        "GET",
        `/performance/traces${qs ? `?${qs}` : ""}`,
      );
    },
    trace: (id: string) =>
      request<RequestTraceDetail>("GET", `/performance/traces/${id}`),
    deleteTrace: (id: string) =>
      request<{ success: boolean }>("DELETE", `/performance/traces/${id}`),
    clearTraces: () =>
      request<{ success: boolean; deleted: number }>(
        "POST",
        "/performance/traces/clear",
      ),
  },
  db: {
    tables: () => request<TableInfo[]>("GET", "/db/tables"),
    tableSchema: (table: string) =>
      request<ColumnInfo[]>("GET", `/db/tables/${table}/schema`),
    tableIndexes: (table: string) =>
      request<IndexInfo[]>("GET", `/db/tables/${table}/indexes`),
    tableForeignKeys: (table: string) =>
      request<FKInfo[]>("GET", `/db/tables/${table}/foreign-keys`),
    query: (sql: string) => request<QueryResult>("POST", "/db/query", { sql }),
    mutate: (sql: string) =>
      request<{
        columns: string[];
        rows: unknown[][];
        rowCount: number;
        changes: number;
        success: boolean;
      }>("POST", "/db/query", { sql }),
    migrations: () => request<MigrationStatus[]>("GET", "/db/migrations"),
    migrationDetail: (name: string) =>
      request<MigrationDetail>(
        "GET",
        `/db/migrations/${encodeURIComponent(name)}`,
      ),
    migrate: () => request<{ applied: string[] }>("POST", "/db/migrate"),
    migrateOne: (name: string) =>
      request<{ applied: string[] }>("POST", `/db/migrate/${name}`),
    seed: (data?: {
      email?: string;
      displayName?: string;
      password?: string;
    }) =>
      request<{ success: boolean; id: string; email: string; message: string }>(
        "POST",
        "/db/seed",
        data,
      ),
  },
  realtime: {
    connections: (
      projectId?: string,
      role?: string,
      organizationId?: string,
    ) => {
      const params = new URLSearchParams();
      if (projectId) params.set("projectId", projectId);
      if (role) params.set("role", role);
      if (organizationId) params.set("organizationId", organizationId);
      const qs = params.toString();
      return fetch(
        managedAppUrl(`/realtime/connections${qs ? `?${qs}` : ""}`),
        { headers: { "Content-Type": "application/json" } },
      ).then((r) => (r.ok ? r.json() : Promise.reject(new Error("Failed"))));
    },
    stats: (projectId?: string) => {
      const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
      return fetch(managedAppUrl(`/realtime/stats${qs}`), {
        headers: { "Content-Type": "application/json" },
      }).then((r) => (r.ok ? r.json() : Promise.reject(new Error("Failed"))));
    },
    debugSessions: {
      list: () =>
        apiFetch<DebugSessionInfo[]>("GET", "/realtime/debug/sessions"),
      get: (id: string) =>
        apiFetch<DebugSessionInfo & { messageCount: number }>(
          "GET",
          `/realtime/debug/sessions/${id}`,
        ),
      messages: (
        sessionId: string,
        params?: {
          limit?: number;
          offset?: number;
          eventType?: string;
          projectId?: string;
        },
      ) => {
        const sp = new URLSearchParams();
        if (params?.limit) sp.set("limit", String(params.limit));
        if (params?.offset) sp.set("offset", String(params.offset));
        if (params?.eventType) sp.set("eventType", params.eventType);
        if (params?.projectId) sp.set("projectId", params.projectId);
        const qs = sp.toString();
        return apiFetch<DebugMessagesResponse>(
          "GET",
          `/realtime/debug/sessions/${sessionId}/messages${qs ? `?${qs}` : ""}`,
        );
      },
      delete: (id: string) =>
        apiFetch<{ success: boolean }>(
          "DELETE",
          `/realtime/debug/sessions/${id}`,
        ),
    },
  },
};
